import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, Folder, Loader2, X, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function UploadModal({ open, onOpenChange, docType, onUploaded }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setSelected(null);
      setBusy(false);
    }
  }, [open]);

  const handleFile = (file) => {
    if (!file) return;
    setSelected(file);
    if (file.type.startsWith("image/")) {
      const url = URL.createObjectURL(file);
      setPreview({ url, type: "image", name: file.name });
    } else {
      setPreview({ type: "pdf", name: file.name });
    }
  };

  const upload = async () => {
    if (!selected || !docType) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("document_type_id", docType.id);
      fd.append("file", selected);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`"${docType.name}" subido. Pendiente de validación.`);
      onUploaded?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al subir");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="upload-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Subir documento</DialogTitle>
          <DialogDescription>{docType?.name}</DialogDescription>
        </DialogHeader>

        {!preview ? (
          <div className="grid gap-3 py-2">
            <input
              ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <input
              ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <Button
              onClick={() => cameraRef.current?.click()}
              className="h-20 bg-primary hover:bg-primary-hover text-white text-base flex-col gap-1.5"
              data-testid="btn-upload-camera"
            >
              <Camera className="h-7 w-7" />
              Tomar foto con la cámara
            </Button>
            <Button
              variant="outline"
              onClick={() => fileRef.current?.click()}
              className="h-20 text-base flex-col gap-1.5 border-2 border-stone-200 hover:bg-stone-50"
              data-testid="btn-upload-file"
            >
              <Folder className="h-6 w-6 text-stone-700" />
              Subir archivo (PDF, imagen)
            </Button>
            <p className="text-xs text-stone-500 text-center mt-1">Tamaño máximo 15MB. Formatos: PDF, JPG, PNG, WEBP.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border-2 border-stone-200 p-3 bg-stone-50">
              {preview.type === "image" ? (
                <img src={preview.url} alt="preview" className="max-h-80 w-full object-contain rounded-md" />
              ) : (
                <div className="flex items-center gap-3 p-4">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Folder className="h-6 w-6 text-primary" />
                  </div>
                  <div className="text-sm font-medium text-stone-800">{preview.name}</div>
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => { setPreview(null); setSelected(null); }} className="flex-1" data-testid="btn-upload-cancel">
                <X className="h-4 w-4 mr-2" /> Cambiar
              </Button>
              <Button onClick={upload} disabled={busy} className="flex-1 bg-primary hover:bg-primary-hover" data-testid="btn-upload-confirm">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-2" /> Confirmar y subir</>}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
